import { useQuery } from '@tanstack/react-query';
import remoteDbService from '@/core/database/RemoteDBService';
import { type Page } from '@/features/pages/types/page.types';
import { useCollectionStore } from '@/features/collections/stores/useCollectionStore';
import { usePageStore } from '@/features/pages/stores/usePageStore';
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

export const usePublicCollectionNote = (pageId: string | undefined) => {
  const currentPage = usePageStore(state => state.currentPage);
  const collectionStructure = useCollectionStore(state => state.collectionStructure);
  const currentCollection = useCollectionStore(state => state.currentCollection);
  const queryClient = useQueryClient();

  const findCurrentPage = useCallback(
    (pageId: string) => {
      // First check if the requested page is already the current page in state
      if (currentPage?.content && currentPage.id === pageId) {
        return currentPage;
      }

      // Check if page exists in cached full pages first
      if (currentCollection?.id) {
        const fullPages = queryClient.getQueryData<Page[]>([
          `collection-notes-full/${currentCollection.id}`,
        ]);
        if (fullPages) {
          const cachedPage = fullPages.find(p => p.id === pageId);
          if (cachedPage) return cachedPage;
        }
      }

      // Otherwise search through collection structure from store (might be metadata-only)
      if (!collectionStructure.length) return null;
      return collectionStructure.flatMap(s => s.section.pages).find(p => p.id === pageId);
    },
    [collectionStructure, currentPage, currentCollection?.id, queryClient]
  );

  return useQuery<Page | null>({
    queryKey: [`public-note/${pageId || ''}`],
    queryFn: async () => {
      if (!pageId) return null;

      const alreadyLoadedPage = findCurrentPage(pageId);
      if (alreadyLoadedPage) {
        // Only return if it has content (not just metadata)
        if ('content' in alreadyLoadedPage && alreadyLoadedPage.content !== undefined) {
          return alreadyLoadedPage as Page;
        }
      }

      // Fall back to fetching individual page
      return remoteDbService.getPublicNote(pageId);
    },
    enabled: !!pageId,
  });
};
